import { Component, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { AuthSignal } from '@xanadu/signal';
import { FormGroup } from '@angular/forms';
import { SecureService } from '@xanadu/service';
import { AccessLevel, Secure, Permission, Permit, Genus, Site, Identity } from '@xanadu/model';
import { filter, switchMap, tap } from 'rxjs/operators';
import { EMPTY, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PanelComponent } from './panel.component';

@Component({
    selector: 'secure',
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class SecureComponent<T extends Secure> extends PanelComponent<T> {
    protected readonly authSignal = inject(AuthSignal);
    protected readonly secureService = inject(SecureService);

    readonly admission = signal<string[] | undefined>(undefined);
    readonly permission = signal<Permission | undefined>(undefined);

    public tabSecurity = { caption: 'Security', active: false, hidden: false };
    public tabAccess = { caption: 'Access', active: false, hidden: false };

    public editable: boolean = false;
    public permissible: boolean = false;
    public permit?: Permit;
    public identity?: Identity;

    public formPermission: FormGroup = new FormGroup({});
    public formAdmission: FormGroup = new FormGroup({});

    protected subjectPermission: Subject<{ type: typeof Genus | typeof Site, permission: any, secure: Secure }> = new Subject<{ type: typeof Genus | typeof Site, permission: any, secure: Secure }>();
    protected subjectCascade: Subject<{ type: typeof Genus | typeof Site, permission: any }> = new Subject<{ type: typeof Genus | typeof Site, permission: any }>();
    protected subjectAdmission: Subject<{ type: typeof Genus | typeof Site, admission: any, secure: Secure }> = new Subject<{ type: typeof Genus | typeof Site, admission: any, secure: Secure }>();

    protected get nodeType(): string | undefined {
        return 'content';
    }

    constructor(
    ) {
        super();

        effect(() => {
            const identity = this.authSignal.identity();

            if (identity) {
                this.identity = identity;
                this.permit = identity.permit;
            }
        });

        this.subjectPermission
            .pipe(
                takeUntilDestroyed(),
                filter(data => !!data),
                switchMap(data => {
                    const permission = this.getPermission(data.permission);
                    const secure = this.node();

                    return secure?._id
                        ? this.secureService.permission(data.type, secure._id, permission)
                            .pipe(
                                tap(_ => {
                                    data.secure.permission = permission;
                                }))
                        : EMPTY;
                }))
            .subscribe(secure => {
                if (secure) {
                    this.noticeSignal.info('Permission saved successfully.');
                    this.formPermission.markAsPristine();
                }
            });

        this.subjectCascade
            .pipe(
                takeUntilDestroyed(),
                filter(data => !!data),
                switchMap(data => {
                    const permission = this.getPermission(data.permission);
                    const secure = this.node();

                    return secure?._id
                        ? this.secureService.cascade(data.type, secure._id, permission)
                            .pipe(
                                tap(secure => {
                                    if (secure && secure) {
                                        this.cascadePermission(secure, permission);
                                    }
                                }))
                        : EMPTY;
                }))
            .subscribe(secure => {
                if (secure) {
                    this.noticeSignal.info('Permission cascaded successfully.');
                    this.formPermission.markAsPristine();
                }
            });

        this.subjectAdmission
            .pipe(
                takeUntilDestroyed(),
                filter(data => !!data),
                switchMap(data => {
                    const admission = data.admission;
                    const secure = this.node();

                    return secure?._id
                        ? this.secureService.admission(data.type, secure._id, admission)
                            .pipe(
                                tap(_ => {
                                    data.secure.admission = admission;
                                }))
                        : EMPTY;
                }))
            .subscribe(secure => {
                if (secure) {
                    this.noticeSignal.info('Admission saved successfully.');
                    this.formAdmission.markAsPristine();
                }
            });
    }

    protected override setup(secure: T): void {
        super.setup(secure);

        this.editable = secure.accessLevel > AccessLevel.List;
        this.permissible = secure.accessLevel === AccessLevel.Manage;

        this.permission.set(secure.permission);
        this.admission.set(secure.admission);
    }

    private getPermission(data: any): Permission {
        const permission = new Permission() as any;
        data.permissions.forEach((x: any) => {
            permission[x.name] = x.roles;
        });

        return permission;
    }

    protected doPermission(type: typeof Genus | typeof Site, secure: Secure): void {
        const entry = this.formPermission.value;
        this.subjectPermission.next({ type, permission: entry, secure });
    }

    protected doCascade(type: typeof Genus | typeof Site): void {
        const entry = this.formPermission.value;
        this.subjectCascade.next({ type, permission: entry });
    }

    protected doAdmission(type: typeof Genus | typeof Site, secure: Secure): void {
        const entry = this.formAdmission.value;
        this.subjectAdmission.next({ type, admission: entry.clients, secure });
    }

    protected cascadePermission(secure: Secure, permission: Permission): void {
        if (secure) {
            secure.permission = permission;

            if (secure.branches?.length) {
                secure.branches
                    .forEach(async branch => {
                        this.cascadePermission(branch as Secure, permission);
                    });
            }
        }
    }
}
